/**
 * Meal-plan subscription charge breakup — mirrors digirasa-api
 * PackagingCharges / DeliveryCharges and mobile cart subscription math
 * (one delivery charge per scheduled delivery trip window).
 */

export const SUBSCRIPTION_GST_PERCENT = 5;

/** Flat charge per delivery trip when the address has no coordinates. */
export const FALLBACK_DELIVERY_CHARGE_PER_TRIP = 20;
/** @deprecated Legacy receipt fallback; delivery is now calculated per trip. */
export const FALLBACK_DELIVERY_CHARGE_PER_DAY = FALLBACK_DELIVERY_CHARGE_PER_TRIP;

export const ONE_MONTH_FREE_DELIVERY_RADIUS_KM = 2;
export const MULTI_MONTH_FREE_DELIVERY_RADIUS_KM = 5;
/** Free delivery expands to 5 km once the plan spans 30+ calendar/delivery days. */
export const FREE_DELIVERY_MIN_DAYS = 30;

/**
 * Slots that travel together count as one delivery trip for the day.
 * Breakfast + mid-morning snack share a trip; lunch + evening snack share a trip;
 * dinner is its own trip.
 */
export const SUBSCRIPTION_DELIVERY_TRIP_WINDOWS = Object.freeze([
  Object.freeze(['breakfast', 'mid_morning']),
  Object.freeze(['lunch', 'evening_snack']),
  Object.freeze(['dinner']),
]);

/** Full-plan calendar rows when Full Day is selected (delivery trips, snacks shown under parent row). */
export const CALENDAR_DELIVERY_TRIP_ROWS = Object.freeze([
  Object.freeze({
    id: 'breakfast',
    label: 'Breakfast · Mid-morn',
    primarySlotId: 'breakfast',
    slotIds: ['breakfast', 'mid_morning'],
  }),
  Object.freeze({
    id: 'lunch',
    label: 'Lunch · Evening',
    primarySlotId: 'lunch',
    slotIds: ['lunch', 'evening_snack'],
  }),
  Object.freeze({
    id: 'dinner',
    label: 'Dinner',
    primarySlotId: 'dinner',
    slotIds: ['dinner'],
  }),
]);

const STANDALONE_CALENDAR_SLOT_IDS = ['breakfast', 'lunch', 'dinner'];

/**
 * Rows for the full-plan calendar table.
 * Full Day → breakfast / lunch / dinner delivery trips with mid-morning & evening snack
 * lines shown inside the matching parent row.
 * À la carte → only active breakfast, lunch, or dinner lines.
 *
 * @returns {Array<{ id: string, label: string, slotIds: string[], products: object[] }>}
 */
export function buildCalendarSlotRows(slotDraftProducts = {}, { hasFullDayPackages = false } = {}) {
  const rows = [];

  if (hasFullDayPackages) {
    CALENDAR_DELIVERY_TRIP_ROWS.forEach((trip) => {
      const products = [];
      trip.slotIds.forEach((slotId) => {
        (slotDraftProducts[slotId] || []).forEach((product) => {
          if (!product?.isFullDayPackage) return;
          products.push({ ...product, slotId });
        });
      });
      if (!products.length) return;
      rows.push({
        id: trip.id,
        label: trip.label,
        slotIds: [...trip.slotIds],
        products,
      });
    });
  }

  STANDALONE_CALENDAR_SLOT_IDS.forEach((slotId) => {
    const standalone = (slotDraftProducts[slotId] || [])
      .filter((product) => !product?.isFullDayPackage)
      .map((product) => ({ ...product, slotId }));
    if (!standalone.length) return;

    const existing = rows.find((row) => row.id === slotId);
    if (existing) {
      existing.products.push(...standalone);
      return;
    }

    const tripMeta = CALENDAR_DELIVERY_TRIP_ROWS.find((trip) => trip.id === slotId);
    rows.push({
      id: slotId,
      label: tripMeta?.label || slotId,
      slotIds: [slotId],
      products: standalone,
    });
  });

  return rows;
}

/**
 * How many delivery trips a set of meal slots produces on one day.
 * @param {Iterable<string>|string[]|null|undefined} slotIds
 * @returns {number}
 */
export function countDeliveryWindowsForSlots(slotIds) {
  const set = new Set(
    [...(slotIds || [])]
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  );
  if (!set.size) return 0;
  return SUBSCRIPTION_DELIVERY_TRIP_WINDOWS.reduce(
    (total, windowSlots) => total + (windowSlots.some((id) => set.has(id)) ? 1 : 0),
    0,
  );
}

/**
 * Sum delivery trips across calendar days.
 * @param {Array<{ slotIds?: Iterable<string>|string[] }|Iterable<string>|string[]>} daySlotLists
 * @returns {number}
 */
export function countDeliveryTripsAcrossDays(daySlotLists = []) {
  return (daySlotLists || []).reduce((total, day) => {
    const slotIds = day && typeof day === 'object' && !Array.isArray(day) && !(day instanceof Set)
      ? day.slotIds
      : day;
    return total + countDeliveryWindowsForSlots(slotIds);
  }, 0);
}

/**
 * Free-delivery radius from duration days (day bands).
 * Legacy month planIds 1/2/3 still map for older snapshots.
 * @param {{ planId?: number|string, deliveryDays?: number }} opts
 */
export function getSubscriptionFreeDeliveryRadiusKm({ planId, deliveryDays } = {}) {
  const days = Number(deliveryDays) || 0;
  if (days > 0) {
    return days >= FREE_DELIVERY_MIN_DAYS
      ? MULTI_MONTH_FREE_DELIVERY_RADIUS_KM
      : ONE_MONTH_FREE_DELIVERY_RADIUS_KM;
  }
  const id = Number(planId);
  if (id === 2 || id === 3) return MULTI_MONTH_FREE_DELIVERY_RADIUS_KM;
  if (id === 1) return ONE_MONTH_FREE_DELIVERY_RADIUS_KM;
  return ONE_MONTH_FREE_DELIVERY_RADIUS_KM;
}

function roundMoney(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** 10% packaging on subtotal — 2 decimal places (not rounded to whole rupees). */
export function packagingChargeForSubtotal(subTotal) {
  const s = Number(subTotal) || 0;
  if (s <= 0) return 0;
  return roundMoney(s * 0.1);
}

/**
 * Delivery slab for one trip window (breakfast+mid-morning, lunch+evening snack,
 * or dinner). Distance-based charge is applied once per trip, not per meal slot.
 * @param {number | null | undefined} distanceKm
 * @param {number} freeDeliveryRadiusKm
 * @returns {number | null} null when distance unknown
 */
export function deliveryChargePerTrip(distanceKm, freeDeliveryRadiusKm) {
  const distance = Number(distanceKm);
  if (!Number.isFinite(distance) || distance < 0) return null;
  if (distance < Number(freeDeliveryRadiusKm)) return 0;
  if (distance < 5) return roundMoney(3 * distance);
  if (distance < 8) return roundMoney(5 * distance);
  return roundMoney(7 * distance);
}

/**
 * @param {{
 *   periodSubtotal: number,
 *   days: number,
 *   deliveryTripCount?: number,
 *   distanceKm?: number | null,
 *   freeDeliveryRadiusKm?: number,
 *   gstPercent?: number,
 *   allowFallbackDelivery?: boolean,
 * }} opts
 */
export function computeSubscriptionPeriodCharges({
  periodSubtotal,
  days,
  deliveryTripCount = 1,
  distanceKm = null,
  freeDeliveryRadiusKm = ONE_MONTH_FREE_DELIVERY_RADIUS_KM,
  gstPercent = SUBSCRIPTION_GST_PERCENT,
  allowFallbackDelivery = true,
}) {
  const daysN = Math.max(1, Number(days) || 1);
  const subtotal = Math.max(0, Number(periodSubtotal) || 0);
  const dailySubtotal = subtotal / daysN;
  const packagingPerDay = packagingChargeForSubtotal(dailySubtotal);
  const totalPackagingCharge = roundMoney(packagingPerDay * daysN);

  const tripCount = Math.max(1, Math.floor(Number(deliveryTripCount) || 1));
  let deliveryPerTrip = 0;
  let deliveryReady = true;
  let usedFallbackDelivery = false;

  const computed = deliveryChargePerTrip(distanceKm, freeDeliveryRadiusKm);
  if (computed == null) {
    if (allowFallbackDelivery) {
      deliveryPerTrip = FALLBACK_DELIVERY_CHARGE_PER_TRIP;
      usedFallbackDelivery = true;
      deliveryReady = false;
    } else {
      deliveryReady = false;
    }
  } else {
    deliveryPerTrip = roundMoney(computed);
  }

  const totalDeliveryCharge = roundMoney(deliveryPerTrip * tripCount);
  const gstRate = Number(gstPercent) || SUBSCRIPTION_GST_PERCENT;
  const gstPerDay = roundMoney((gstRate / 100) * (dailySubtotal + packagingPerDay));
  const totalTax = roundMoney(gstPerDay * daysN);
  const totalPrice = roundMoney(subtotal + totalTax + totalDeliveryCharge + totalPackagingCharge);
  const perDayPrice = daysN > 0 ? roundMoney(totalPrice / daysN) : totalPrice;
  const pricePerSlot = roundMoney(totalPrice / tripCount);
  const numericDistance = Number.isFinite(Number(distanceKm)) ? Number(distanceKm) : null;
  const isFreeDeliveryPlan = numericDistance != null && numericDistance < Number(freeDeliveryRadiusKm);

  return {
    subtotal,
    totalTax,
    totalPackagingCharge,
    totalDeliveryCharge,
    totalPrice,
    packagingPerDay: roundMoney(packagingPerDay),
    deliveryPerTrip: roundMoney(deliveryPerTrip),
    gstPerDay,
    gstPercent: gstRate,
    days: daysN,
    deliveryTripCount: tripCount,
    perDayPrice,
    pricePerSlot,
    deliveryReady,
    usedFallbackDelivery,
    isFreeDeliveryPlan,
    freeDeliveryRadiusKm: Number(freeDeliveryRadiusKm),
    distanceKm: numericDistance,
  };
}
